import { useEffect, useState, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { bibleState } from '@/state/bible-state';
import { GearIcon, DownloadIcon, CloseIcon, PlusIcon } from '@components/icons';
import { StudyList } from './study-list/study-list';
import { StudySettingsModal } from './study-settings-modal';
import { ExportModal } from './export-modal';
import { StudyEditor } from './study-editor';

/**
 * Study panel - left-side drawer for Bible study management.
 */
export const StudyPanel = observer(function StudyPanel() {
  const [shouldRender, setShouldRender] = useState(false);
  const [animationState, setAnimationState] = useState<'entering' | 'exiting' | 'visible'>(
    'exiting',
  );
  const [navDirection, setNavDirection] = useState<'forward' | 'back' | null>(null);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const prevShowEditorRef = useRef(false);

  const isVisible = bibleState.studyPanelVisible;
  const editorMode = bibleState.studyEditorMode;

  // Show editor when in create or edit mode
  const showEditor = editorMode === 'create' || editorMode === 'edit';

  // Track navigation direction for animations
  useEffect(() => {
    if (showEditor !== prevShowEditorRef.current) {
      setNavDirection(showEditor ? 'forward' : 'back');
      const timer = setTimeout(() => setNavDirection(null), 300);
      prevShowEditorRef.current = showEditor;
      return () => clearTimeout(timer);
    }
  }, [showEditor]);

  // Handle visibility changes with animation
  useEffect(() => {
    if (isVisible) {
      setShouldRender(true);
      setAnimationState('entering');
      const timer = setTimeout(() => setAnimationState('visible'), 400);
      return () => clearTimeout(timer);
    } else {
      setAnimationState('exiting');
      const timer = setTimeout(() => setShouldRender(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isVisible]);

  if (!shouldRender) return null;

  const navClass =
    navDirection === 'forward' ? 'nav-forward' : navDirection === 'back' ? 'nav-back' : '';

  return (
    <div
      className={`study-panel ${animationState === 'entering' ? 'entering' : ''} ${animationState === 'exiting' ? 'closing' : ''}`}
    >
      <div className={`study-panel-nav-container ${navClass}`}>
        {/* Close button — top right corner */}
        <button
          onClick={() => bibleState.hideStudyPanel()}
          className="-mr-1 -mt-1 mb-1 self-end rounded-full p-2 text-stone-500 transition-all hover:bg-black/5 hover:text-stone-700 dark:text-stone-400 dark:hover:bg-white/10 dark:hover:text-stone-200"
        >
          <CloseIcon className="h-5 w-5" />
        </button>

        {showEditor ? (
          // Editor view (push navigation)
          <div className="study-panel-page">
            <StudyEditor />
          </div>
        ) : (
          <div className="study-panel-page">
            {/* Title */}
            <span className="mb-2 text-lg font-semibold text-stone-800 dark:text-cyan-100">
              Studies
            </span>

            {/* Header — gear / download / plus grouped on the left */}
            <div className="study-panel-header">
              <div className="study-segment-control">
                <button
                  onClick={() => bibleState.openStudySettings()}
                  data-guide="study-settings"
                  className={`study-segment ${bibleState.studySettingsOpen ? 'active' : ''}`}
                  title="Groups & Students"
                >
                  <GearIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setIsExportOpen(true)}
                  className={`study-segment ${isExportOpen ? 'active' : ''}`}
                  title="Export & Import"
                >
                  <DownloadIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => bibleState.startCreateStudy()}
                  data-guide="create-study"
                  className="study-segment"
                  title="Create new study"
                >
                  <PlusIcon className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Studies list — always the panel body */}
            <div className="study-panel-content">
              <StudyList />
            </div>

          </div>
        )}
      </div>

      <StudySettingsModal />
      {isExportOpen && <ExportModal onClose={() => setIsExportOpen(false)} />}
    </div>
  );
});

export default StudyPanel;
